import type { ToolDefinition, ToolType } from "../types";

const HEIC_TO_JPG_TOOL_TYPE = { label: "iPhone Photo Converter", icon: "Smartphone" } as const satisfies ToolType;

const heicToJpgTool: ToolDefinition = {
    slug: "heic-to-jpg",
    toolType: HEIC_TO_JPG_TOOL_TYPE,
    heroBadges: [
      "100% Free",
      "iPhone Compatible",
      "Three Compression Presets",
      "Metadata Removed",
      "No Watermark",
      "Works on Mobile"
    ],
    name: "HEIC to JPG / PNG Converter Online | Convert iPhone HEIC Photos — 3 Quality Presets",
    description: "Convert iPhone HEIC photos to JPG or PNG — three quality presets: Low (quality 90), Medium (quality 75), or High (quality 55). Metadata stripped automatically.",
    longDescription: "Convert HEIC images from iPhone, iPad, and Mac to universally compatible JPG or PNG. Three compression presets control the quality-versus-file-size balance: Low (JPEG quality 90, up to 4000px — maximum fidelity), Medium (quality 75, up to 3000px — balanced), High (quality 55, up to 2000px — smallest file). All presets strip EXIF metadata, correct orientation automatically, and normalise DPI to 72. Output filename appends '_compressed'. Free, no watermark.",
    category: "Conversion Tools",
    categorySlug: "conversion-tools",
    href: "/tools/heic-to-jpg",
    api: "/heic-to-image",
    keywords: [
        "heic to jpg",
        "heic to png",
        "iphone photo converter",
        "convert apple photos",
        "jpg converter for iphone",
        "png export from heic",
        "snappy fix heic converter",
        "ios image format tool",
        "camera photo converter",
        "batch heic converter",
        "quality controlled converter",
        "heic image tool"
      ],
    pageContent: {
      sections: {
        steps: {
          title: "How to Convert HEIC to JPG or PNG",
          description: "Four steps. Under 15 seconds. Works directly from your iPhone."
        },
        benefits: {
          title: "Why Use the Snappy-Fix HEIC Converter?",
          description: "Three compression presets, automatic metadata removal, and direct iPhone support — fast and private."
        },
        faqs: {
          description: "Everything you need to know about HEIC and converting iPhone photos."
        }
      },
      intro: {
        title: "Why Can't You Open HEIC Photos Everywhere?",
        paragraphs: [
          "Since iOS 11, iPhones and iPads save photos in HEIC format by default. HEIC (High Efficiency Image Container) produces files that are roughly half the size of JPEG at equivalent quality — a genuine improvement for storage-conscious devices. The problem is that HEIC is an Apple-proprietary format that most of the world's devices and platforms simply do not support.",
          "Windows requires a codec extension to open many HEIC files. Android often cannot open them natively. Many websites reject HEIC uploads. Email clients struggle to preview them. Design tools like Figma and older Photoshop versions may require extra support. Converting your HEIC photos to JPG or PNG solves all of these problems instantly.",
        ],
      },
      callouts: [
        {
          title: "Convert to JPG when:",
          iconColor: "primary",
          items: [
            { label: "Sharing photos via messaging or email", icon: "Share2" },
            { label: "Uploading to social media or websites", icon: "Globe" },
            { label: "Saving storage space on your device", icon: "FolderArchive" },
            { label: "Sending to someone on Windows or Android", icon: "MonitorSmartphone" },
          ],
        },
        {
          title: "Convert to PNG when:",
          iconColor: "accent",
          items: [
            { label: "Editing the photo in Photoshop or Figma", icon: "Palette", accent: "accent" },
            { label: "Preserving exact colours with no compression", icon: "ShieldCheck", accent: "accent" },
            { label: "The image contains text or sharp graphics", icon: "Type", accent: "accent" },
            { label: "You need a lossless archival copy", icon: "HardDrive", accent: "accent" },
          ],
        },
      ],
      referenceTables: [
        {
          title: "Compression presets — quality vs file size",
          description:
            "Every preset strips EXIF metadata, corrects orientation, and normalises DPI to 72. PNG output remains lossless.",
          columns: [
            "Preset",
            "JPEG quality",
            "Max width",
            "Best for",
            "Output size",
          ],
          emphasisColumns: ["JPEG quality"],
          monospaceColumns: ["JPEG quality"],
          rows: [
            {
              Preset: "Low compression",
              "JPEG quality": "90",
              "Max width": "4000px",
              "Best for": "Archival, print, maximum fidelity",
              "Output size": "Largest",
            },
            {
              Preset: "Medium compression",
              "JPEG quality": "75",
              "Max width": "3000px",
              "Best for": "Professional sharing, balanced use",
              "Output size": "Medium",
            },
            {
              Preset: "High compression",
              "JPEG quality": "55",
              "Max width": "2000px",
              "Best for": "Social media, email, messaging",
              "Output size": "Smallest",
            },
          ],
        },
        {
          title: "HEIC vs JPG / PNG compatibility across platforms",
          note: "* Windows 10/11 often needs the HEVC Video Extensions codec from the Microsoft Store for HEIC support.",
          columns: ["Platform", "HEIC", "JPG", "PNG"],
          rows: [
            { Platform: "Windows 10 / 11", HEIC: "Limited*", JPG: "✓", PNG: "✓" },
            { Platform: "Android", HEIC: "✗", JPG: "✓", PNG: "✓" },
            { Platform: "macOS", HEIC: "✓", JPG: "✓", PNG: "✓" },
            { Platform: "iOS / iPadOS", HEIC: "✓", JPG: "✓", PNG: "✓" },
            { Platform: "Chrome / Firefox", HEIC: "✗", JPG: "✓", PNG: "✓" },
            { Platform: "Social media uploads", HEIC: "✗", JPG: "✓", PNG: "✓" },
            { Platform: "Email attachments", HEIC: "✗", JPG: "✓", PNG: "✓" },
            { Platform: "Photoshop / Figma", HEIC: "Plugin required", JPG: "✓", PNG: "✓" },
          ],
        },
      ],
      faqs: [
  {
    question: "What is HEIC and why can't I open it on Windows or Android?",
    answer:
      "HEIC (High Efficiency Image Container) is Apple's proprietary photo format introduced with iOS 11. It produces smaller file sizes than JPEG at the same quality, which is why iPhones use it by default. However, Windows, Android devices, and most websites do not natively support HEIC — you need to convert it to JPG or PNG before sharing or uploading to non-Apple platforms.",
  },
  {
    question: "Should I convert HEIC to JPG or PNG?",
    answer:
      "Choose JPG if you want smaller file sizes — ideal for sharing via messaging apps, email, or uploading to social media. JPG uses lossy compression so file sizes are much smaller. Choose PNG if you need lossless quality with no compression artefacts — better for editing, design work, screenshots, or images with text and sharp edges. Both formats are universally supported across all devices and platforms.",
  },
  {
    question: "What do the compression presets (Low, Medium, High) do?",
    answer:
      "The tool offers three compression levels that control the trade-off between output file size and image quality. Low compression produces the largest file at the highest quality (JPEG quality 90, up to 4000px wide) — use this for archival copies, print use, or when you need maximum fidelity. Medium compression is a balanced setting (JPEG quality 75, up to 3000px wide) — suitable for most sharing and professional use. High compression produces the smallest file (JPEG quality 55, up to 2000px wide) — use this for social media, email, and messaging where file size matters most. All three presets strip metadata and normalise EXIF orientation automatically.",
  },
  {
    question: "Will converting HEIC to JPG reduce the image quality?",
    answer:
      "It depends on the compression preset chosen. At Low compression (quality 90), the visual difference from the original is virtually invisible. At Medium (quality 75), the result is appropriate for most sharing and web use with minimal visible artefacts. At High compression (quality 55), some artefacts may be visible on close inspection — this preset is best for contexts where file size matters more than pixel-perfect quality. Converting to PNG is always lossless regardless of preset.",
  },
  {
    question: "Does the tool resize my image?",
    answer:
      "The tool applies a maximum width limit based on the compression preset chosen: Low compression keeps images up to 4000px wide, Medium up to 3000px, and High up to 2000px. If your original HEIC photo is smaller than the limit for your chosen preset, no resizing is applied. Aspect ratio is always preserved — the image is never distorted. For a 12MP iPhone photo (4032×3024px), Low compression keeps the full resolution, Medium reduces it slightly, and High brings it to 2000px on the longest side.",
  },
  {
    question: "Does converting HEIC remove metadata from my photo?",
    answer:
      "Yes. The conversion process strips all EXIF metadata from the output image — including GPS location, device model, timestamps, and camera settings. EXIF orientation is read first (to correctly rotate the image) and then discarded along with all other metadata. The output image is also saved with 72 DPI (the standard web resolution). If you need to preserve metadata, note that this tool performs a full metadata strip as part of conversion.",
  },
  {
    question: "How do I get HEIC photos off my iPhone?",
    answer:
      "On iPhone, go to Settings > Photos and change the transfer format to 'Most Compatible' — this makes your iPhone send JPG files when you AirDrop or cable-transfer photos to non-Apple devices. Alternatively, upload your HEIC file directly to this converter from your iPhone using Safari and download the converted JPG or PNG to your device.",
  },
  {
    question: "Are my uploaded HEIC photos stored on your servers?",
    answer:
      "No. Your HEIC file is processed securely and not permanently retained after conversion is complete. We do not store, index, or share your photos. The converted image is returned directly to your browser.",
  },
  {
    question: "Can I convert HEIC photos taken on iPad as well?",
    answer:
      "Yes. HEIC is the default photo format for all modern Apple devices — iPhone, iPad, and Mac cameras all produce HEIC files. Upload any HEIC photo from any Apple device and convert it to JPG or PNG instantly.",
  },
  {
    question: "Does this HEIC converter work on my iPhone or Android phone?",
    answer:
      "Yes. The converter works in any modern mobile browser. On iPhone, open Safari, navigate to this page, tap the upload zone, choose your HEIC photo from your library, convert it, and download the JPG or PNG directly to your Camera Roll. On Android, you can open HEIC files sent to you by iPhone users and convert them to a format your device can use natively.",
  },
  {
    question: "Is this tool free and does it add a watermark?",
    answer:
      "Yes — completely free with no usage limits and no watermark on converted images. Convert as many HEIC photos as you need at no cost with no registration required.",
  },
],
      benefits: [
  {
    icon: "Sliders",
    title: "Three compression presets",
    description:
      "Low (quality 90, up to 4000px), Medium (quality 75, up to 3000px), and High (quality 55, up to 2000px) — choose the right balance of quality and file size for your use case.",
  },
  {
    icon: "FileImage",
    title: "JPG and PNG output",
    description:
      "Choose JPG for smaller file sizes perfect for sharing, or PNG for lossless quality with no compression artefacts.",
  },
  {
    icon: "Zap",
    title: "Metadata stripped automatically",
    description:
      "EXIF orientation is applied to correctly rotate your photo, then all metadata — GPS, device info, timestamps — is removed from the output.",
  },
  {
    icon: "Globe",
    title: "Universal compatibility",
    description:
      "Converted JPG and PNG files open on Windows, Android, Linux, all web browsers, and every image editing app.",
  },
  {
    icon: "ShieldCheck",
    title: "Private and secure",
    description:
      "Your photos are processed securely and never permanently stored. What you upload stays completely private.",
  },
  {
    icon: "Smartphone",
    title: "Works from iPhone",
    description:
      "Use this tool directly from your iPhone in Safari — upload, convert, and download back to your Camera Roll without a computer.",
  },
],
      steps: [
  {
    number: "01",
    title: "Upload your HEIC file",
    description:
      "Drag and drop your HEIC photo onto the upload zone or click to browse your device. The tool accepts .heic files — the format used by iPhones and iPads.",
  },
  {
    number: "02",
    title: "Choose format and compression",
    description:
      "Select JPG (smaller, universally compatible) or PNG (lossless). Then choose your compression level — Low for maximum quality, Medium for balanced output, High for smallest file size.",
  },
  {
    number: "03",
    title: "Click Convert Image",
    description:
      "Hit the Convert Image button. A progress bar tracks your upload and conversion in real time. The tool corrects orientation, strips metadata, resizes if needed, and returns your converted file.",
  },
  {
    number: "04",
    title: "Download and use anywhere",
    description:
      "Click Download Converted Image to save the file — named with '_compressed' appended so it is easy to identify alongside the original. The output is now compatible with Windows, Android, all websites, and every image editor.",
  },
],
    },
  };

export default heicToJpgTool;
export { heicToJpgTool };
